/**
 * /admin/setup-manual — 16-step onboarding playbook for new clinics.
 *
 * Iter 130 — Each step now renders as a shadcn accordion item so the
 * whole 16-step playbook fits on one screen at a glance. Search filters
 * the list AND auto-expands every matched step so the term is visible.
 *
 * Each step has an optional video slot fed by clinic settings (`setup_videos.{id}`).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BookOpen, Search } from 'lucide-react';
import { SETUP_STEPS } from './content';
import { renderBody, SetupVideo } from './helpers';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../../components/ui/accordion';

export function ClinicSetupManual() {
  const [q, setQ] = useState('');
  const location = useLocation();
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return SETUP_STEPS;
    return SETUP_STEPS.filter(s =>
      s.title.toLowerCase().includes(needle) || s.body.toLowerCase().includes(needle)
    );
  }, [q]);

  const [open, setOpen] = useState([]);

  // Search-driven auto-expand.
  useEffect(() => {
    if (q.trim()) {
      setOpen(filtered.map(s => s.id));
    }
  }, [q, filtered]);

  // Hash-driven deep-link — open the matching step and scroll to it.
  useEffect(() => {
    const hash = location.hash.slice(1);
    if (!hash) return;
    if (SETUP_STEPS.some(s => s.id === hash)) {
      setOpen(prev => (prev.includes(hash) ? prev : [...prev, hash]));
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 250);
    }
  }, [location.hash]);

  const totalMin = SETUP_STEPS.reduce((acc, s) => acc + (parseInt(s.duration) || 0), 0);

  return (
    <div className="p-4 md:p-8 max-w-5xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">REFERENCE</div>
        <h1 className="text-4xl font-heading font-bold tracking-tight inline-flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-brand"/> Clinic setup manual
        </h1>
        <p className="text-fg-muted mt-2">{SETUP_STEPS.length}-step onboarding playbook for new clinics. Estimated total time: ~{totalMin} minutes (excluding Stripe identity verification).</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted pointer-events-none"/>
          <input type="search" value={q} onChange={e=>setQ(e.target.value)}
                 placeholder="Search the setup manual…" className="input pl-9 w-full" data-testid="setup-search"/>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setOpen(filtered.map(s => s.id))}
            className="btn-secondary text-xs"
            data-testid="setup-expand-all"
          >
            Expand all
          </button>
          <button
            type="button"
            onClick={() => setOpen([])}
            className="btn-secondary text-xs"
            data-testid="setup-collapse-all"
          >
            Collapse all
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-fg-muted text-center py-12">No steps match.</div>
      ) : (
        <Accordion
          type="multiple"
          value={open}
          onValueChange={setOpen}
          className="border-t border-soft"
          data-testid="setup-accordion"
        >
          {filtered.map((step) => (
            <AccordionItem
              key={step.id}
              value={step.id}
              id={step.id}
              className="border-soft"
              data-testid={`setup-step-${step.id}`}
            >
              <AccordionTrigger className="text-left" data-testid={`setup-step-trigger-${step.id}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="shrink-0 w-8 h-8 rounded-md bg-brand text-white font-heading font-bold text-sm flex items-center justify-center">
                    {SETUP_STEPS.indexOf(step) + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold truncate">{step.title}</div>
                    <div className="text-[11px] text-fg-muted uppercase tracking-wider mt-0.5">~ {step.duration}</div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="text-sm text-fg-secondary whitespace-pre-wrap leading-relaxed" dangerouslySetInnerHTML={{ __html: renderBody(step.body) }}/>
                <SetupVideo videoKey={step.id}/>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
}
